<template>
    <div class="card video-stream-doctor">
        <div class="card-body">
            <div class="row">
                <div class="col-md-6">
                    <div class="label-user">
                        <span class="small">{{ $locale.patient }}:&nbsp;{{ user ? user.full_name : `退会済の${$locale.patient}` }}</span>
                        <div class="p-devices">
                            <div class="c-icon c-icon--microphone-off"
                                 :class="{ 'c-icon--microphone-live' : remoteUserAudioState }">
                            </div>
                            <div class="c-icon c-icon--movie-off"
                                 :class="{ 'c-icon--movie-live' : remoteUserVideoState }">
                            </div>
                        </div>
                    </div>

                    <fullscreen ref="fullscreen-user" :fullscreen.sync="fullscreenUser">
                        <div class="videoContainer" :class="{ 'videoContainer--fluid' : !fullscreenUser && remoteUserVideo !== null }">
                            <div class="videoContainer__inner">
                                <div id="remote-video-user"></div>

                                <div v-if="remoteUserVideo !== null">
                                    <div
                                        class="c-icon c-icon--fullscreen"
                                        @click="fullscreenToggle('fullscreen-user')">
                                    </div>
                                </div>
                                <div
                                    v-else
                                    class="videoContainer__dummy">
                                    <img
                                        class="videoContainerDummyImage"
                                        src="/images/common/profile_default.svg">
                                </div>
                            </div>
                        </div>
                    </fullscreen>
                </div>

                <div class="col-md-6">
                    <div class="label-doctor">
                        <span class="small">医師:&nbsp;{{doctor.full_name}}</span>
                        <div class="p-devices">
                            <div class="c-icon c-icon--microphone-off"
                                 :class="{ 'c-icon--microphone-live' : remoteDoctorAudioState }">
                            </div>
                            <div class="c-icon c-icon--movie-off"
                                 :class="{ 'c-icon--movie-live' : remoteDoctorVideoState }">
                            </div>
                        </div>
                    </div>

                    <fullscreen ref="fullscreen-doctor" :fullscreen.sync="fullscreenDoctor">
                        <div class="videoContainer" :class="{ 'videoContainer--fluid' : !fullscreenDoctor && remoteDoctorVideo !== null }">
                            <div class="videoContainer__inner">
                                <div id="remote-video-doctor"></div>

                                <div v-if="remoteDoctorVideo !== null">
                                    <div
                                        class="c-icon c-icon--fullscreen"
                                        @click="fullscreenToggle('fullscreen-doctor')">
                                    </div>
                                </div>
                                <div
                                    v-else
                                    class="videoContainer__dummy">
                                    <img src="/images/common/profile_default.svg">
                                </div>
                            </div>
                        </div>
                    </fullscreen>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import AgoraRTC from 'agora-rtc-sdk-ng'
    import constants from "../../constants"
    import Fullscreen from "vue-fullscreen/src/component.vue"

    /**
     * デフォルトが DEBUG なので NONE にする（importするだけで全画面ログが出てしまうため）
     *
     * - 0: DEBUG. Output all API logs.
     * - 1: INFO. Output logs of the INFO, WARNING and ERROR level.
     * - 2: WARNING. Output logs of the WARNING and ERROR level.
     * - 3: ERROR. Output logs of the ERROR level.
     * - 4: NONE. Do not output any log.
     */
    AgoraRTC.setLogLevel(4)

    export default {
        props: {
            roomKey: {
                type: String,
                default: '',
            },
            apiKey: {
                type: String,
                default: '',
            },
            token: {
                type: String,
                default: '',
            },
            // @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/clientconfig.html#mode
            transcode: {
                type: String,
                default: 'rtc',
            },
            debug: {
                type: String,
                default: '0',
            },
            user: {
                type: Object|null,
                required: true,
            },
            doctor: {
                type: Object,
                required: true,
            },
            consultation: {
                type: Object,
                required: true,
            },
        },

        components: {
            Fullscreen,
        },

        data: function () {
            return {
                client: null,
                remoteUserVideo: null,
                remoteUserAudio: null,
                remoteUserVideoState: false,
                remoteUserAudioState: false,
                remoteDoctorVideo: null,
                remoteDoctorAudio: null,
                remoteDoctorVideoState: false,
                remoteDoctorAudioState: false,
                timer: null,
                fullscreenUser: false,
                fullscreenDoctor: false,
            }
        },

        computed: {
            /**
             * agoraセッションにおけるユニークID
             * 先頭が3の場合は管理者と見なす
             *
             * @return {number}
             */
            uid: function () {
                const id = '3' + this.consultation.id.toString()
                return Number(id)
            },

            /**
             * デバッグモード判定
             *
             * @return {boolean}
             */
            isDebug: function () {
                return this.debug === '1'
            },
        },

        created: function () {
            bus.$on('admin-agora-init', this.initAgora)
        },

        beforeDestroy: function () {
            bus.$off('admin-agora-init')
        },

        methods: {
            /**
             * 初期化処理
             */
            initAgora: async function () {
                if (this.client !== null) return

                /**
                 * @type {IAgoraRTCClient}
                 * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/iagorartc.html#createclient
                 * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/clientconfig.html
                 */
                this.client = AgoraRTC.createClient({ mode: this.transcode, codec: 'h264' })
                this.subscribeStreamEvents()

                /**
                 * @see https://docs.agora.io/en/Video/API%20Reference/web_ng/interfaces/iagorartcclient.html#join
                 */
                await this.client.join(this.apiKey, this.roomKey, this.token, this.uid)
                    .then((uid) => {
                        console.log('[agora] join success.', this.isDebug ? `uid:${uid}` : '')
                    })
                    .catch((e) => {
                        console.log('[agora] client join failed.', this.isDebug ? e : '')
                    })
            },

            /**
             * uid頭文字を取得
             *
             * @param {number} uid
             * @return {string}
             */
            initialStrNum: function (uid) {
                return (uid + '').slice(0, 1)
            },

            /**
             * Agoraのイベントリスナ追加
             */
            subscribeStreamEvents: function () {
                this.client.on('user-published', async (remoteUser, mediaType) => {
                    const initialUid = this.initialStrNum(remoteUser.uid)

                    // 管理者の場合
                    if (initialUid === '3') {
                        return
                    }

                    await this.client.subscribe(remoteUser, mediaType)

                    if (mediaType === 'video') {
                        if (initialUid === '1') {
                            console.log('[agora] subscribe user video success')
                            this.remoteUserVideo = remoteUser.videoTrack
                            this.remoteUserVideo.play('remote-video-user')
                        }
                        else if (initialUid === '2') {
                            console.log('[agora] subscribe doctor video success')
                            this.remoteDoctorVideo = remoteUser.videoTrack
                            this.remoteDoctorVideo.play('remote-video-doctor')
                        }
                    }
                    if (mediaType === 'audio') {
                        if (initialUid === '1') {
                            console.log('[agora] subscribe user audio success')
                            this.remoteUserAudio = remoteUser.audioTrack
                            this.remoteUserAudio.play()
                        }
                        else if (initialUid === '2') {
                            console.log('[agora] subscribe doctor audio success')
                            this.remoteDoctorAudio = remoteUser.audioTrack
                            this.remoteDoctorAudio.play()
                        }
                    }
                    this.updateRemoteState()
                })

                this.client.on('user-unpublished', async (remoteUser, mediaType) => {
                    const initialUid = this.initialStrNum(remoteUser.uid)

                    // 管理者の場合
                    if (initialUid === '3') {
                        return
                    }

                    await this.client.unsubscribe(remoteUser, mediaType)

                    if (mediaType === 'video') {
                        if (initialUid === '1') {
                            console.log('[agora] unsubscribe user video success')
                            this.remoteUserVideo = null
                        }
                        else if (initialUid === '2') {
                            console.log('[agora] unsubscribe doctor video success')
                            this.remoteDoctorVideo = null
                        }
                    }
                    if (mediaType === 'audio') {
                        if (initialUid === '1') {
                            console.log('[agora] unsubscribe user audio success')
                            this.remoteUserAudio = null
                        }
                        else if (initialUid === '2') {
                            console.log('[agora] unsubscribe doctor audio success')
                            this.remoteDoctorAudio = null
                        }
                    }
                    this.clearRemoteMedia(mediaType, initialUid)
                    this.updateRemoteState()
                })
            },

            /**
             * 患者・医師の接続メディアをクリア
             *
             * @param {string} mediaType
             * @param {string} initialUid
             */
            clearRemoteMedia: function (mediaType = 'all', initialUid) {
                if (mediaType === 'video' || mediaType === 'all') {
                    if (initialUid === '1') {
                        this.remoteUserVideo = null
                    }
                    else if (initialUid === '2') {
                        this.remoteDoctorVideo = null
                    }
                }

                if (mediaType === 'audio' || mediaType === 'all') {
                    if (initialUid === '1') {
                        this.remoteUserAudio = null
                    }
                    else if (initialUid === '2') {
                        this.remoteDoctorAudio = null
                    }
                }
            },

            /**
             * 患者・医師のデバイス接続ステータスを更新
             */
            updateRemoteState: function () {
                setTimeout(() => {
                    this.remoteUserVideoState = this.remoteUserVideo && this.remoteUserVideo.isPlaying
                    this.remoteUserAudioState = this.remoteUserAudio && this.remoteUserAudio.isPlaying

                    this.remoteDoctorVideoState = this.remoteDoctorVideo && this.remoteDoctorVideo.isPlaying
                    this.remoteDoctorAudioState = this.remoteDoctorAudio && this.remoteDoctorAudio.isPlaying
                }, 1000)
            },

            /**
             * フルスクリーン用のトグルイベント
             *
             * @param {string} ref
             */
            fullscreenToggle: function (ref) {
                this.$refs[ref].toggle()
            },
        },
    }
</script>

<style lang="scss">
.video-stream-doctor {
    .videoContainer {
        position: relative;
        width: 100%;

        &::before {
            content: '';
            display: block;
            padding-top: 75%;
        }

        .videoContainer__inner {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
        }

        .videoContainer__dummy {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background: #f1f1f1;
            text-align: center;

            .videoContainerDummyImage {
                height: 90% !important;
            }
        }

        #local-video {
            display: block;
            width: 100%;
            height: 100%;
        }

        #remote-video-user,
        #remote-video-doctor {
            display: block;
            width: 100%;
            height: 100%;
        }

        &.videoContainer--fluid {
            height: auto;

            &::before {
                content: '';
                display: none;
                padding-top: 0;
            }

            .videoContainer__inner {
                position: static;
                width: 100%;
                height: auto;
            }

            #local-video {
                > div {
                    width: 100% !important;
                    height: auto !important;
                    background: transparent !important;
                }

                video {
                    position: static !important;
                    width: 100% !important;
                    height: auto !important;
                }
            }

            #remote-video-user,
            #remote-video-doctor {
                > div {
                    width: 100% !important;
                    height: auto !important;
                    background: transparent !important;
                }

                video {
                    position: static !important;
                    width: 100% !important;
                    height: auto !important;
                }
            }
        }

        .c-icon--fullscreen {
            position: absolute;
            right: 0;
            bottom: 6px;
            width: 28px;
            height: 28px;
            cursor: pointer;
        }

        .video-capture {
            position: absolute;
            left: 0;
            bottom: 6px;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 28px;
            padding: 0 6px;
            color: #fff;
            font-size: .75rem;
            background: #244787;
            cursor: pointer;

            &.video-capture--progress {
                opacity: .5;
                pointer-events: none;
            }
        }

        .c-icon--camera {
            width: 18px;
            height: 18px;
            margin-right: 4px;
            background-color: transparent;
            background-size: cover;

            + div {
                line-height: 28px;
            }
        }

        span {
            margin: auto;
        }
    }

    .selectedChangeContainer {
        div {
            width: 100%;
        }
    }

    .p-devices {
        > div {
            display: inline-block;
            vertical-align: middle;
        }

        .c-icon--microphone-on,
        .c-icon--microphone-off,
        .c-icon--movie-on,
        .c-icon--movie-off {
            position: relative;
            width: 24px;
            height: 24px;
            border-radius: 4px;

            &::before {
                content: '';
                display: none;
                position: absolute;
                left: 0;
                top: -27px;
                z-index: 2;
                height: 24px;
                padding-left: 8px;
                line-height: 24px;
                color: #fff;
                font-size: .75rem;
                letter-spacing: 0;
                box-sizing: border-box;
                border-radius: 4px;
            }
        }

        .c-icon--microphone-on,
        .c-icon--microphone-off {
            margin-right: 4px;
        }

        .c-icon--microphone-off:hover {
            &::before {
                content: 'マイクが認識できません';
                display: block;
                width: 150px;
                background: #dc3545;
            }
        }

        .c-icon--movie-off:hover {
            &::before {
                content: 'カメラが認識できません';
                display: block;
                width: 150px;
                background: #dc3545;
            }
        }

        .c-icon--microphone-on:hover {
            &::before {
                content: 'マイクが認識されています（待機中）';
                display: block;
                width: 220px;
                background: #2fa69a;
            }
        }

        .c-icon--movie-on:hover {
            &::before {
                content: 'カメラが認識されています（待機中）';
                display: block;
                width: 220px;
                background: #2fa69a;
            }
        }

        .c-icon--microphone-live:hover {
            &::before {
                content: 'マイクが認識されています';
                display: block;
                width: 170px;
                background: #2fa69a;
            }
        }

        .c-icon--movie-live:hover {
            &::before {
                content: 'カメラが認識されています';
                display: block;
                width: 170px;
                background: #2fa69a;
            }
        }
    }
}
</style>
